import {
  Article,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { getReferenceTransitionParts } from "@babackapp/return-variables-registry";
import { DiscountedPrice } from "@babackapp/return-widget-components/components/discounted-price";
import { getLineItemDisplay } from "@babackapp/return-widget-components/utils/line-item-display";
import {
  useLocalizeLineItem,
  useLocalizeProductOptions,
} from "@babackapp/return-widget-components/hooks/use-localize-line-item";
import type {
  DraftLineItem,
  Order,
  OrderLineItem,
} from "@babackapp/return-widget-components/types";
import { ExchangeArticleImage, getExchangeArticle } from "./exchange-article";
import { ReferenceFooter } from "./reference-footer";

type Props = {
  item: DraftLineItem;
  orderLineItems: OrderLineItem[];
  order: Order | null;
  currency: string;
  /** Purchased → chosen SKU, as on the summary (`useProductReferencesVisibility`). */
  showReferences?: boolean;
  /** "Only you see this" tooltip, when the logged-in merchant alone sees the SKUs. */
  referencesHint?: string;
};

export function CompleteReturnLineItem({
  item,
  orderLineItems,
  order,
  currency,
  showReferences = false,
  referencesHint,
}: Props) {
  const localize = useLocalizeLineItem();
  const translateOptions = useLocalizeProductOptions();
  const display = getLineItemDisplay(
    orderLineItems.map(localize),
    item.lineItemId,
  );
  const { title, image, price, paidPrice, sku } = display;

  const { variantDisplay, exchangeTitle, exchangeImage, exchangeSku } =
    getExchangeArticle(order, item, display, translateOptions);
  const references = showReferences
    ? getReferenceTransitionParts(sku, exchangeSku)
    : null;

  return (
    <Article>
      <ExchangeArticleImage
        src={image}
        exchangeSrc={exchangeImage}
        exchangeAlt={exchangeTitle ?? title}
        layoutId={`img-complete-${item.lineItemId}`}
      />
      <ArticleTitle>
        {title}
        {exchangeTitle && (
          <span className="font-normal text-muted-foreground">
            {" "}
            → {exchangeTitle}
          </span>
        )}
      </ArticleTitle>
      <ArticleSubtitle>
        {variantDisplay && `${variantDisplay} · `}×{item.quantity}
        {!!price && (
          <>
            {" · "}
            <DiscountedPrice
              catalogCents={price}
              paidCents={paidPrice}
              quantity={item.quantity}
              currency={currency}
            />
          </>
        )}
      </ArticleSubtitle>
      {references && <ReferenceFooter {...references} hint={referencesHint} />}
    </Article>
  );
}
